import { create } from "zustand";
import type { CountryDto } from "../types/country";
import { getAllCountries } from "../services/countryService";
import { LIVE_COUNTRIES, toCountries, type Country } from "../utils/network";

/** How long a fetched network stays fresh (SPEC 7: cache 1 hour). */
export const NETWORK_TTL_MS = 60 * 60 * 1000;

export const NETWORK_CACHE_KEY = "np.network.v1";

interface NetworkCache {
  fetchedAt: number;
  countries: Country[];
}

function readCache(now: number): NetworkCache | null {
  try {
    if (typeof sessionStorage === "undefined") return null;
    const raw = sessionStorage.getItem(NETWORK_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as NetworkCache;
    if (
      !parsed ||
      typeof parsed.fetchedAt !== "number" ||
      !Array.isArray(parsed.countries) ||
      parsed.countries.length === 0 ||
      now - parsed.fetchedAt >= NETWORK_TTL_MS
    ) {
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

function writeCache(cache: NetworkCache) {
  try {
    if (typeof sessionStorage === "undefined") return;
    sessionStorage.setItem(NETWORK_CACHE_KEY, JSON.stringify(cache));
  } catch {
    // Storage full or blocked: the in-memory copy is enough.
  }
}

interface CountryStoreState {
  /** Raw API rows for the signed-in app (ids, phone codes, ...). */
  countries: CountryDto[];
  countriesLoading: boolean;
  loaded: boolean;
  /** Fetch the raw country list (used by /app forms and pickers). */
  fetchCountries: () => Promise<void>;

  /**
   * The public network, always populated: seeded from the bundled
   * LIVE_COUNTRIES (or a fresh session cache) and replaced by the API list
   * once it arrives. Never empty, so pages and prerendered HTML have content.
   */
  network: Country[];
  networkSource: "fallback" | "cache" | "api";
  /** When the network was last fetched from the API (ms epoch), else null. */
  networkFetchedAt: number | null;
  /**
   * Refresh the network in the background. No-op while the last fetch is
   * younger than NETWORK_TTL_MS unless `force` is set. Never throws; on
   * failure the current (fallback or cached) list stays.
   */
  refreshNetwork: (opts?: { force?: boolean }) => Promise<void>;
}

function initialNetwork(): Pick<CountryStoreState, "network" | "networkSource" | "networkFetchedAt"> {
  const cache = readCache(Date.now());
  if (cache) {
    return { network: cache.countries, networkSource: "cache", networkFetchedAt: cache.fetchedAt };
  }
  return { network: LIVE_COUNTRIES, networkSource: "fallback", networkFetchedAt: null };
}

let inflight: Promise<void> | null = null;

export const useCountryStore = create<CountryStoreState>((set, get) => {
  /** Apply a fresh API list to both views and the session cache. */
  const applyApiList = (rows: CountryDto[]) => {
    const network = toCountries(rows);
    if (network.length === 0) return;
    const fetchedAt = Date.now();
    set({ network, networkSource: "api", networkFetchedAt: fetchedAt });
    writeCache({ fetchedAt, countries: network });
  };

  return {
    countries: [],
    countriesLoading: false,
    loaded: false,
    fetchCountries: async () => {
      set({ countriesLoading: true });
      const countries = await getAllCountries();
      set({ countries, countriesLoading: false, loaded: true });
      applyApiList(countries);
    },

    ...initialNetwork(),
    refreshNetwork: async ({ force = false } = {}) => {
      const { networkFetchedAt } = get();
      if (!force && networkFetchedAt !== null && Date.now() - networkFetchedAt < NETWORK_TTL_MS) {
        return;
      }
      if (inflight) return inflight;
      inflight = (async () => {
        try {
          const rows = await getAllCountries();
          if (rows.length > 0) {
            // Share the rows with /app consumers so they skip their own fetch.
            if (!get().loaded) set({ countries: rows, loaded: true });
            applyApiList(rows);
          }
        } catch (err) {
          console.error("[countryStore] refreshNetwork error", err);
        } finally {
          inflight = null;
        }
      })();
      return inflight;
    },
  };
});
